import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  getExercise,
  sendReviewMessage,
  regenerateRender,
  validateExercise,
} from "../services/exerciseAiService";
import ChatMarkdown from "./ChatMarkdown";

const ROLE_STYLES = {
  teacher: { align: "flex-start", bg: "#6f4bd8", color: "#fff", label: "" },
  assistant: { align: "flex-end", bg: "#f3e8ff", color: "#111", label: "Darssy AI" },
};

export default function TeacherReviewChat({ groupId, exerciseId, onClose, onValidated }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [messages, setMessages] = useState([]);
  const [reviewStatus, setReviewStatus] = useState("pending");
  const [candidateRenderText, setCandidateRenderText] = useState("");
  const [validatedRenderText, setValidatedRenderText] = useState("");
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState("");
  const [hasLoadedMessages, setHasLoadedMessages] = useState(false);
  const bottomRef = useRef(null);

  const refreshExerciseState = () => {
    getExercise({ groupId, exerciseId }).then(({ exercise }) => {
      setReviewStatus(exercise.reviewStatus || "pending");
      setCandidateRenderText(exercise.candidateRenderText || "");
      setValidatedRenderText(exercise.validatedRenderText || "");
    });
  };

  useEffect(() => {
    refreshExerciseState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, exerciseId]);

  useEffect(() => {
    if (!groupId || !exerciseId) return undefined;

    const messagesRef = query(
      collection(db, "groups", groupId, "exercises", exerciseId, "reviewMessages"),
      orderBy("createdAt", "asc")
    );
    const unsubscribe = onSnapshot(messagesRef, (snapshot) => {
      setMessages(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setHasLoadedMessages(true);
    });

    return unsubscribe;
  }, [groupId, exerciseId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (hasLoadedMessages && messages.length === 0 && !isRegenerating) {
      handleRegenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoadedMessages, messages.length]);

  const handleSend = async () => {
    const messageToSend = input.trim();
    if (!messageToSend || isSending) return;
    setError("");
    setIsSending(true);
    setInput("");

    try {
      await sendReviewMessage({ groupId, exerciseId, message: messageToSend });
    } catch (err) {
      setError(err.message || t("تعذّر إرسال الرسالة."));
    } finally {
      setIsSending(false);
    }
  };

  const handleRegenerate = async () => {
    setError("");
    setIsRegenerating(true);
    try {
      const { renderText } = await regenerateRender({ groupId, exerciseId });
      setCandidateRenderText(renderText);
    } catch (err) {
      setError(err.message || t("تعذّر تحديث الفهم."));
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleValidate = async () => {
    setError("");
    setIsValidating(true);
    try {
      await validateExercise({ groupId, exerciseId });
      refreshExerciseState();
      onValidated?.();
    } catch (err) {
      setError(err.message || t("تعذّر التصديق على التمرين."));
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>{t("مراجعة والتحقق من Darssy AI")}</span>
        <Chip
          size="small"
          color={reviewStatus === "validated" ? "success" : "warning"}
          icon={reviewStatus === "validated" ? <CheckCircleIcon /> : undefined}
          label={reviewStatus === "validated" ? t("تم التحقق") : t("غير مُراجَع")}
        />
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, direction: "rtl" }}>
        <Box
          sx={{
            height: { xs: "55vh", sm: 320 },
            minHeight: { xs: "55vh", sm: 320 },
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            p: 1,
            border: "1px solid #e5e7eb",
            borderRadius: 2,
          }}
        >
          {messages.map((message) => {
            const roleStyle = ROLE_STYLES[message.role] || ROLE_STYLES.teacher;
            return (
              <Box
                key={message.id}
                sx={{
                  alignSelf: roleStyle.align,
                  bgcolor: roleStyle.bg,
                  color: roleStyle.color,
                  borderRadius: 3,
                  px: 1.5,
                  py: 1,
                  maxWidth: "85%",
                }}
              >
                {roleStyle.label && (
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#7c3aed" }}>
                    {roleStyle.label}
                  </Typography>
                )}
                <ChatMarkdown content={message.content} />
              </Box>
            );
          })}
          {isRegenerating && messages.length === 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          <div ref={bottomRef} />
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t("صحّح أو استفسر عن فهم Darssy AI...")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            disabled={isSending}
          />
          <IconButton color="primary" onClick={() => handleSend()} disabled={isSending}>
            <SendIcon />
          </IconButton>
        </Box>

        <Button
          variant="outlined"
          startIcon={<AutorenewIcon />}
          onClick={handleRegenerate}
          disabled={isRegenerating}
        >
          {isRegenerating ? t("جارٍ التحديث...") : t("تحديث الفهم")}
        </Button>

        {candidateRenderText && (
          <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>
              {t("فهم Darssy AI الحالي (قبل التصديق)")}
            </Typography>
            <ChatMarkdown content={candidateRenderText} sx={{ fontSize: 14 }} />
          </Box>
        )}

        {reviewStatus === "validated" && validatedRenderText && (
          <Typography sx={{ fontSize: 12, color: "#16a34a" }}>
            {t("تم التصديق على هذا الفهم، وهو المعتمد حالياً مع الطلاب.")}
          </Typography>
        )}

        {error && <Typography sx={{ color: "red", textAlign: "center" }}>{error}</Typography>}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t("إغلاق")}</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleValidate}
          disabled={!candidateRenderText || isValidating}
        >
          {isValidating ? t("جارٍ التصديق...") : t("تصديق ونشر للطلاب")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
